import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [viewMoreButton] - The "Load more" button (load-more pagination mode).
 * @property {HTMLElement} [loadMoreStatus] - The "Showing X of Y" status (load-more pagination mode).
 * @property {HTMLElement} [loadMoreProgress] - The catalog-progress fill bar (load-more pagination mode).
 * @property {HTMLButtonElement} [loadMoreToTop] - The floating back-to-top button (load-more pagination mode).
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /** sessionStorage key prefix; the full key is per-list (section id + filtered path) so
   *  visiting a second PLP can't clobber the first shopper's snapshot (and two lists on one
   *  page — e.g. search results + the empty-state popular grid — keep separate entries). */
  static #RESTORE_PREFIX = 'ca-plp-restore:';

  /** Max snapshot size we're willing to persist; above this we defer to the browser (avoids QuotaExceeded). */
  static #RESTORE_MAX_BYTES = 2_000_000;

  /** How long a snapshot stays valid (30 min) before we ignore it as stale. */
  static #RESTORE_TTL_MS = 30 * 60 * 1000;

  /** Keep at most this many list snapshots at once (oldest evicted) to bound sessionStorage. */
  static #RESTORE_MAX_ENTRIES = 4;

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // Prewarm adjacent pages into the `pages` cache so the first load-more
    // click / infinite-scroll trigger appends instantly. Deferred to idle so
    // these section-render fetches don't compete with the LCP image and other
    // critical-path requests on the initial PLP/search paint. The render paths
    // (#renderNextPage / #renderPreviousPage) re-trigger their own fetch if the
    // page isn't cached yet, so behavior is unchanged if the click beats idle.
    requestIdleCallback(() => {
      if (!this.isConnected) return;
      this.#fetchPage('next');
      this.#fetchPage('previous');
    });
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // --- Back-navigation scroll + loaded-pages restoration ---------------------
    // "Load more" / infinite scroll appends pages via pushState (no real navigation),
    // so on Back the browser rebuilds the PLP with only page 1 and can't find the
    // deep scroll offset — dumping the shopper near the top. We take scroll control
    // ourselves: snapshot the expanded grid + scroll on the way out (pagehide) and
    // rehydrate it on Back/reload (pageshow), so shoppers land exactly where they left.
    // scrollRestoration is per history-entry, so setting 'manual' here only affects PLP
    // entries — non-PLP pages keep the UA default. (No reset needed unless a PLP is ever
    // section-swapped in without a full load; not a path today.)
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('pagehide', this.#onPageHide);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    window.removeEventListener('pagehide', this.#onPageHide);
  }

  /**
   * Per-list sessionStorage key: prefix + section id + pathname + facet/sort params
   * (minus the transient `page`/hash). Scoping the *storage key itself* — not just a
   * field inside the value — means a snapshot only ever restores into the exact same
   * list + filtered view, and a second PLP visit writes its own entry instead of
   * clobbering the first.
   * @returns {string}
   */
  #storageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = '';
    const sectionId = this.getAttribute('section-id') ?? '';
    return `${PaginatedList.#RESTORE_PREFIX}${sectionId}:${url.pathname}${url.search}`;
  }

  /**
   * Drop expired snapshots and, if we're still at the entry cap, the oldest — leaving
   * room for the one about to be written so sessionStorage stays bounded.
   */
  #pruneSnapshots() {
    const now = Date.now();
    /** @type {{ k: string, ts: number }[]} */
    const live = [];
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (!k || !k.startsWith(PaginatedList.#RESTORE_PREFIX)) continue;
      let ts = 0;
      try {
        ts = JSON.parse(sessionStorage.getItem(k) || '{}').ts || 0;
      } catch {
        // Unparseable — treat as dead and remove.
      }
      if (!ts || now - ts > PaginatedList.#RESTORE_TTL_MS) {
        sessionStorage.removeItem(k);
        continue;
      }
      live.push({ k, ts });
    }
    live.sort((a, b) => b.ts - a.ts);
    for (const { k } of live.slice(PaginatedList.#RESTORE_MAX_ENTRIES - 1)) sessionStorage.removeItem(k);
  }

  /**
   * Persist the expanded grid + scroll offset when the shopper leaves the PLP
   * (clicking into a product, or reloading — pagehide also fires on bfcache entry).
   */
  #onPageHide = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const html = grid.innerHTML;
    // Oversized snapshot (very deep infinite scroll): skip rather than risk a
    // QuotaExceededError. Note: because we hold scrollRestoration at 'manual', these
    // skipped sessions land at the top on Back/reload rather than at the saved offset
    // — a deliberate tradeoff (a >2MB restore isn't worth the storage risk).
    if (html.length > PaginatedList.#RESTORE_MAX_BYTES) return;

    try {
      this.#pruneSnapshots();
      sessionStorage.setItem(
        this.#storageKey(),
        JSON.stringify({
          href: window.location.href,
          html,
          scrollY: window.scrollY,
          cardCount: grid.querySelectorAll(':scope > [ref="cards[]"]').length,
          ts: Date.now(),
        })
      );
    } catch {
      // Private mode / quota — non-fatal; fall back to browser default.
    }
  };

  /**
   * On page show, restore the shopper's place.
   * - bfcache restore (persisted): the expanded DOM is already live, but our
   *   `scrollRestoration = 'manual'` suppresses the UA's own scroll restore — so we
   *   reapply just the saved offset (no innerHTML rewrite).
   * - full reload / back-forward rebuild: replay the snapshot (grid + scroll).
   * A fresh navigation starts at the top by design, so we leave it alone.
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (event.persisted) {
      const data = this.#readSnapshot();
      this.#consumeSnapshot();
      if (data) this.#restoreScroll(Number(data.scrollY) || 0);
      return;
    }

    const nav = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    if (nav?.type !== 'back_forward' && nav?.type !== 'reload') return;

    this.#restoreFromCache();
  };

  /**
   * Read + validate this list's snapshot (key/type/TTL) without consuming it.
   * @returns {{ href?: string, html: string, scrollY?: number, cardCount?: number, ts: number } | null}
   */
  #readSnapshot() {
    let raw;
    try {
      raw = sessionStorage.getItem(this.#storageKey());
    } catch {
      return null;
    }
    if (!raw) return null;

    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
    if (!data || typeof data.html !== 'string') return null;
    if (!data.ts || Date.now() - data.ts > PaginatedList.#RESTORE_TTL_MS) return null;
    return data;
  }

  /** One-shot consume so a later fresh visit to the same list doesn't inherit a stale scroll. */
  #consumeSnapshot() {
    try {
      sessionStorage.removeItem(this.#storageKey());
    } catch {
      // ignore
    }
  }

  /**
   * Replay the snapshot on a full rebuild: re-render the expanded grid, rebuild refs,
   * drop the now-misaligned prewarmed pages, sync controls, and restore scroll.
   */
  #restoreFromCache() {
    const { grid } = this.refs;
    if (!grid) return;

    const data = this.#readSnapshot();
    this.#consumeSnapshot();
    if (!data) return;

    const currentCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;

    // Only rewrite the grid when the snapshot actually holds MORE cards than the
    // freshly-rendered page 1 — i.e. the shopper had expanded it. A plain reload of an
    // un-expanded grid skips the innerHTML churn (custom-element re-upgrade) and just
    // restores scroll below.
    if ((data.cardCount ?? 0) > currentCount) {
      grid.innerHTML = data.html;

      // Rebuild refs synchronously. On pageshow the base component's MutationObserver
      // isn't observing yet (it starts in a requestIdleCallback), so without this
      // refs.cards would keep pointing at the detached page-1 cards and mis-drive
      // #getPage — appending an already-shown page. updatedCallback() is the same
      // takeRecords()+#updateRefs() path the Section Rendering API uses.
      this.updatedCallback();
      // The idle prefetch computed `pages` from the stale page-1 refs; clear it so the
      // next load-more/infinite trigger recomputes the correct next page.
      this.pages.clear();
      this.#aspectRatioHelper?.processNewElements();

      // Realign the URL/state with the pages we just restored (e.g. ?page=3).
      if (typeof data.href === 'string') history.replaceState('', '', data.href);

      this.#syncControlsAfterRestore();
    }

    this.#restoreScroll(Number(data.scrollY) || 0);
  }

  /**
   * Restore the scroll offset now and again after layout settles. Square, aspect-
   * ratioed product cards keep height stable, so the second pass is belt-and-suspenders.
   * @param {number} y
   */
  #restoreScroll(y) {
    window.scrollTo({ top: y, behavior: 'instant' });
    requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' }));
  }

  /**
   * Bring the "Load more" button, status text, and progress bar in line with a
   * restored (multi-page) grid, without emitting a live-region "N more loaded"
   * announcement — the shopper is returning, not actively loading.
   */
  #syncControlsAfterRestore() {
    const { viewMoreButton, loadMoreStatus, loadMoreProgress, grid } = this.refs;
    if (!grid) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastPage = Number(grid.dataset.lastPage);
    const lastCard = cards[cards.length - 1];
    const currentPage = lastCard ? Number(lastCard.getAttribute('data-page')) : 1;

    // Retire the button if the restored grid already reaches the final page.
    if (viewMoreButton && (!lastPage || currentPage >= lastPage)) viewMoreButton.remove();

    if (loadMoreStatus) {
      const total = loadMoreStatus.dataset.total ?? `${cards.length}`;
      const countTemplate = loadMoreStatus.dataset.statusTemplate;
      loadMoreStatus.textContent = countTemplate
        ? countTemplate.replace('%shown%', `${cards.length}`).replace('%total%', total)
        : `${cards.length} / ${total}`;
    }

    if (loadMoreProgress) {
      const totalNum = Number(loadMoreStatus?.dataset.total);
      if (totalNum > 0) {
        loadMoreProgress.style.width = `${Math.min(100, (cards.length / totalNum) * 100)}%`;
      }
    }

    this.#updateToTopVisibility();
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Load the next page in place (load-more pagination mode). Triggered by the
   * "Load more" button's delegated `on:click="/loadMore"` binding. Reuses the
   * same append + pushState path as infinite scroll, then refreshes the status
   * and retires the button once the final page has been appended.
   */
  async loadMore() {
    const { viewMoreButton, loadMoreStatus } = this.refs;

    // Re-entrancy guard: the button (unlike the infinite-scroll sentinel) stays in
    // place during the fetch, so a rapid double-click could resolve the same
    // nextPage twice and append a duplicate page. Bail if a load is already busy.
    if (viewMoreButton?.getAttribute('aria-busy') === 'true') return;

    // Snapshot before the append: the current page boundary (to locate the first
    // card of the page we're about to add) and whether focus is on the button (so
    // we only redirect focus if the user is actually on it when it retires).
    const pageBefore = this.#currentMaxPage();
    const focusWasOnButton = viewMoreButton ? viewMoreButton.contains(document.activeElement) : false;

    if (viewMoreButton) {
      viewMoreButton.setAttribute('aria-busy', 'true');
      // aria-disabled (NOT `disabled`): blocks the action semantically while
      // keeping the button focusable, so focus isn't dropped to <body> mid-fetch.
      // The re-entrancy guard above prevents an actual double-fire.
      viewMoreButton.setAttribute('aria-disabled', 'true');
    }
    // Announce progress for slow loads; the result count overwrites this on done.
    if (loadMoreStatus?.dataset.loadingText) {
      loadMoreStatus.textContent = loadMoreStatus.dataset.loadingText;
    }

    await this.#renderNextPage();
    const buttonRetired = this.#updateLoadMoreState(pageBefore);
    this.#updateToTopVisibility();

    if (viewMoreButton?.isConnected) {
      viewMoreButton.removeAttribute('aria-busy');
      viewMoreButton.removeAttribute('aria-disabled');
    } else if (buttonRetired && focusWasOnButton) {
      // The button removed itself on the final page while holding focus — move
      // focus into the content the user asked for (first card of the new page)
      // rather than dropping them to <body>/page top (WCAG 2.4.3).
      this.#focusFirstCardOfPage(pageBefore + 1);
    }
  }

  /**
   * Reveal the floating back-to-top button once the load-more list has grown
   * long enough (~2 pages) that returning to the top is a real chore. Once
   * shown it stays; idempotent.
   */
  #updateToTopVisibility() {
    const { loadMoreToTop, grid } = this.refs;
    if (!loadMoreToTop || !grid) return;

    const count = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    if (count >= 48) loadMoreToTop.classList.add('load-more__to-top--visible');
  }

  /**
   * Scroll back to the top of the page and move keyboard focus to the top of the
   * results so keyboard users continue from there (not stranded on the floating
   * button). Honors prefers-reduced-motion. Bound via `on:click="/scrollToTop"`.
   */
  scrollToTop() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

    const focusTarget = this.querySelector('#ResultsList') ?? this;
    focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
  }

  /**
   * The highest page number currently present in the grid (load-more mode).
   * @returns {number}
   */
  #currentMaxPage() {
    const { grid } = this.refs;
    const cards = grid?.querySelectorAll(':scope > [ref="cards[]"]');
    const last = cards?.[cards.length - 1];
    return last ? Number(last.getAttribute('data-page')) : 1;
  }

  /**
   * Move focus to the first card of a freshly-appended page so keyboard / screen
   * reader users land on the content they requested instead of <body> when the
   * "Load more" button retires (WCAG 2.4.3). scroll-margin-top on the card keeps
   * it clear of the sticky header (2.4.11).
   * @param {number} page
   */
  #focusFirstCardOfPage(page) {
    const { grid } = this.refs;
    const firstNew = grid?.querySelector(`:scope > [ref="cards[]"][data-page="${page}"]`);
    if (!firstNew) return;

    const target = firstNew.querySelector('a[href]');
    if (target) {
      target.focus({ preventScroll: true });
    } else {
      // CA's product-card-editorial always has a title link, so this is defensive.
      firstNew.setAttribute('tabindex', '-1');
      firstNew.focus({ preventScroll: true });
    }
    firstNew.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  /**
   * After a load-more click, refresh the "Showing X of Y" status and remove the
   * button when there are no further pages to load.
   */
  #updateLoadMoreState(pageBefore) {
    const { viewMoreButton, loadMoreStatus, loadMoreProgress, grid } = this.refs;
    if (!grid) return false;

    // Read the grid DOM directly: refs.cards is rebuilt asynchronously by the
    // component's MutationObserver, so it can still be stale immediately after
    // #renderNextPage() appends the new page.
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const lastCard = cards[cards.length - 1];
    const lastPage = Number(grid.dataset.lastPage);
    const currentPage = lastCard ? Number(lastCard.getAttribute('data-page')) : 1;

    let buttonRetired = false;
    if (viewMoreButton && (!lastPage || currentPage >= lastPage)) {
      viewMoreButton.remove();
      buttonRetired = true;
    }

    if (loadMoreStatus) {
      const total = loadMoreStatus.dataset.total ?? `${cards.length}`;
      const countTemplate = loadMoreStatus.dataset.statusTemplate;
      const showing = countTemplate
        ? countTemplate.replace('%shown%', `${cards.length}`).replace('%total%', total)
        : `${cards.length} / ${total}`;

      // On a non-final load, prefix the delta ("N more products loaded.") so the
      // live region tells SR users what just happened, not only the new total. On
      // the final load we keep it to the count and let the focus move (in
      // loadMore) be the headline, avoiding a double announcement.
      const newCards = grid.querySelectorAll(`:scope > [ref="cards[]"][data-page="${pageBefore + 1}"]`);
      const loadedTemplate = loadMoreStatus.dataset.loadedTemplate;
      loadMoreStatus.textContent =
        !buttonRetired && loadedTemplate && newCards.length
          ? `${loadedTemplate.replace('%count%', `${newCards.length}`)} ${showing}`
          : showing;
    }

    if (loadMoreProgress) {
      // Only update when we have an authoritative total (paginate.items, stamped
      // on the status as data-total). If it's ever missing, leave the
      // server-rendered width rather than defaulting to a misleading 100%.
      const totalNum = Number(loadMoreStatus?.dataset.total);
      if (totalNum > 0) {
        loadMoreProgress.style.width = `${Math.min(100, (cards.length / totalNum) * 100)}%`;
      }
    }

    return buttonRetired;
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    // Optional: the aspect-ratio helper is only created when a [ref="cardGallery"]
    // template card exists (see connectedCallback). CA's product-card-editorial has
    // no such ref, so the helper is undefined here — guard it, or the throw aborts
    // the pushState + status update below (products append but URL/counter stall).
    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Re-assert `initialized` synchronously, up front. This covers the case where
    // the facet/sort Section-Rendering fetch is throttled, fails, or otherwise never
    // lands: no morph runs, so `initialized` is never stripped and this assert holds.
    // When the morph DOES land it strips this JS-set attribute again (server HTML
    // lacks it; morph.js copyAttributes removes old-only attrs, and copyAttributes
    // runs whenever isEqualNode is false — which it is, precisely because of this
    // attribute). The grid MutationObserver below restores it on the resulting grid
    // mutation. The two asserts are complementary — neither alone covers a landed
    // morph that leaves the grid byte-identical with an unchanged data-last-page
    // (no observer mutation fires); that case is only reachable in theory today, as
    // a real facet change alters the product set. Idempotent.
    if (this.isConnected && !this.hasAttribute('initialized')) {
      this.setAttribute('initialized', '');
    }

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Re-assert the `initialized` attribute on every grid mutation. The facet/
      // sort morph reconciles <results-list>'s attributes against the server HTML,
      // which never contains `initialized` (it's JS-set in connectedCallback), so
      // the morph strips it (morph.js copyAttributes removes old-only attributes).
      // connectedCallback does NOT re-fire because the element is morphed in place,
      // not recreated — so without this, the load-more button's `[initialized]`-
      // gated visibility is lost and the no-JS numeric fallback reappears. Done on
      // every mutation (not gated on the data-last-page change below) so it also
      // survives filter changes that leave the page count unchanged.
      if (this.isConnected && !this.hasAttribute('initialized')) {
        this.setAttribute('initialized', '');
      }

      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
